import type { StrokeStyle, Color } from 'pixi.js';
import { Container } from 'pixi.js';
import { ScoreBubbleLayout } from '../../../layouts/scoring/ScoreBubbleLayout';
import { GAME_OBJECT_POOL } from '../GameConstants';
import { applyLayoutState, resetLayout } from '../../libs/layouts/populateLayouts/applyLayoutState';
import { resetContainer } from '../../utils/pixi/resetContainer';

export interface TaskScoreBubbleTextColors {
  text: Color;
  stroke: Color;
}

export class TaskScoreBubbleView extends Container {
  public static getFreeInstance(): TaskScoreBubbleView {
    return GAME_OBJECT_POOL.getObject(TaskScoreBubbleView) ?? new TaskScoreBubbleView();
  }

  public static returnInstance(bubble: TaskScoreBubbleView): void {
    bubble.removeTweens(true);
    GAME_OBJECT_POOL.addObject(TaskScoreBubbleView, bubble.reset());
  }

  private readonly layout = new ScoreBubbleLayout();

  constructor() {
    super();

    const { root } = this.layout;
    this.addChild(root);
  }

  private reset(): this {
    resetLayout(this.layout);
    resetContainer(this);
    return this;
  }

  public init(colors: TaskScoreBubbleTextColors, points: number, final: boolean): this {
    if (final) applyLayoutState(this.layout, 'final');
    this.setPoints(points, final);
    this.setTextColors(colors);
    return this;
  }

  private setPoints(points: number, final: boolean): void {
    const { pointsText } = this.layout;
    if (points >= 14 || final) {
      applyLayoutState(this.layout, 'big');
    } else if (points >= 12) {
      applyLayoutState(this.layout, 'medium');
    }

    pointsText.text = `+${Math.floor(points)}`;
  }

  private setTextColors(colors: TaskScoreBubbleTextColors): void {
    const { pointsText } = this.layout;
    const strokeStyle = pointsText.style.stroke as StrokeStyle;
    pointsText.style.fill = colors.text;
    pointsText.style.stroke = {
      color: colors.stroke,
      width: strokeStyle.width,
    };
  }

  public async fadeBackground(toAlpha: number, duration: number, delay = 0, toScale = 1): Promise<void> {
    const { bubbleBg } = this.layout;
    await bubbleBg
      .tweenTo({ scaleX: toScale, scaleY: toScale, alpha: toAlpha }, duration, 'quadOut', true, delay)
      .asyncOnComplete();
  }
}
